import React, { ComponentProps } from 'react';

import { Box, CenteredLayout, PancakeStack, Separator, WorkflowHeader } from '@betterment/samba-react';
import { Helmet } from 'react-helmet';

interface WorkflowLayoutProps {
  pageTitle: string;
}

/**
 * Workflow Layout component for fullscreen workflow pages.
 * Does not include sidebar or standard navigation.
 * Used for multi-step flows and takeover experiences.
 *
 * Usage:
 * <WorkflowLayout pageTitle="My Workflow">
 *   <WorkflowLayout.Header>
 *     <WorkflowLayout.Leading>Logo/back button</WorkflowLayout.Leading>
 *     <WorkflowLayout.Title>Workflow Title</WorkflowLayout.Title>
 *     <WorkflowLayout.Trailing>Close button</WorkflowLayout.Trailing>
 *   </WorkflowLayout.Header>
 *   <WorkflowLayout.Main>
 *     Main content here
 *   </WorkflowLayout.Main>
 *   <WorkflowLayout.Footer>
 *     Footer content here
 *   </WorkflowLayout.Footer>
 * </WorkflowLayout>
 */
const Layout: React.FC<React.PropsWithChildren<WorkflowLayoutProps>> = ({ pageTitle, children }) => {
  return (
    <PancakeStack
      css={{
        maxWidth: '100%',
        height: '100%',
        position: 'relative',
        width: '100vw',
        minHeight: '100dvh',
        backgroundColor: 'background.surface',
      }}
    >
      <Helmet titleTemplate="%s | Betterment at Work" defaultTitle="Betterment at Work">
        <title>{pageTitle}</title>
      </Helmet>
      {children}
    </PancakeStack>
  );
};

const Header: React.FC<React.PropsWithChildren> = ({ children }) => (
  <PancakeStack.Header
    css={{
      maxWidth: '100%',
      display: 'flex',
      position: 'sticky',
      top: '0',
      justifyContent: 'center',
      borderBottom: 'solid',
      borderWidth: 'thin',
      borderColor: 'border',
    }}
  >
    <WorkflowHeader>{children}</WorkflowHeader>
  </PancakeStack.Header>
);

const Leading: React.FC<React.PropsWithChildren> = ({ children }) => (
  <WorkflowHeader.Leading>{children}</WorkflowHeader.Leading>
);

const Title: React.FC<React.PropsWithChildren> = ({ children }) => (
  <WorkflowHeader.Title>{children}</WorkflowHeader.Title>
);

const Trailing: React.FC<React.PropsWithChildren> = ({ children }) => (
  <WorkflowHeader.Trailing>{children}</WorkflowHeader.Trailing>
);

interface MainProps {
  centered?: boolean;
  centeredSize?: ComponentProps<typeof CenteredLayout>['size'];
}

const Main: React.FC<React.PropsWithChildren<MainProps>> = ({ children, centered = true, centeredSize = 'medium' }) => (
  <PancakeStack.Main>
    {centered ? (
      <CenteredLayout size={centeredSize} css={{ py: 'space.600' }}>
        {children}
      </CenteredLayout>
    ) : (
      children
    )}
  </PancakeStack.Main>
);

interface FooterProps {
  centered?: boolean;
  centeredSize?: ComponentProps<typeof CenteredLayout>['size'];
  withSeparator?: boolean;
}

const Footer: React.FC<React.PropsWithChildren<FooterProps>> = ({
  children,
  centered = true,
  centeredSize = 'medium',
  withSeparator = true,
}) => (
  <PancakeStack.Footer css={{ position: 'sticky', bottom: '0', width: '100%', background: 'background.surface' }}>
    {withSeparator && <Separator />}
    {centered ? (
      <CenteredLayout size={centeredSize} css={{ p: 'space.200' }}>
        {children}
      </CenteredLayout>
    ) : (
      children
    )}
  </PancakeStack.Footer>
);

const Content: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box css={{ flex: 1, overflow: 'auto' }}>{children}</Box>
);

type WorkflowLayoutComposite = typeof Layout & {
  Header: typeof Header;
  Leading: typeof Leading;
  Title: typeof Title;
  Trailing: typeof Trailing;
  Main: typeof Main;
  Footer: typeof Footer;
  Content: typeof Content;
};

export const WorkflowLayout = Layout as WorkflowLayoutComposite;
WorkflowLayout.Header = Header;
WorkflowLayout.Leading = Leading;
WorkflowLayout.Title = Title;
WorkflowLayout.Trailing = Trailing;
WorkflowLayout.Main = Main;
WorkflowLayout.Footer = Footer;
WorkflowLayout.Content = Content;
